import { Injectable, inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { DocketService } from '../../shared/index';
import { Docket } from '../../models/index';
import {
  combineLatest,
  distinctUntilChanged,
  map,
  mergeMap,
  Observable,
  switchMap,
  tap
} from 'rxjs';
import { tapResponse } from '@ngrx/operators';

interface Pagination {
  page: number;
  perPage: number;
  count: number;
  sortBy: string;
  sortOrder: string;
}

interface State {
  dockets: Docket[];
  selectedDocketIds: number[];
  loadingResults: boolean;
  pagination: Pagination;
  datasource: Observable<any[]>;
  summaryData: any;
  filters: {
    created?: {
      from: string | null;
      to: string | null;
    };
    closed?: {
      from: string | null;
      to: string | null;
    };
    status: {
      type: string;
      value: {
        operator: string;
        value: string;
      };
    };
  };
}

const defaultState: State = {
  dockets: [],
  selectedDocketIds: [],
  loadingResults: false,
  pagination: {
    page: 0,
    perPage: 10,
    count: 0,
    sortBy: 'id',
    sortOrder: 'asc'
  },
  datasource: null,
  summaryData: {},
  filters: {
    status: {
      type: 'dropDown',
      value: {
        operator: '=',
        value: 'Active'
      }
    }
  }
};

@Injectable()
export class BatchInvoiceReportStoreService extends ComponentStore<State> {
  constructor() {
    super(defaultState);
  }

  readonly docketService = inject(DocketService);

  // Selectors
  readonly loadingResults$ = this.select(
    ({ loadingResults }) => loadingResults
  );
  readonly dockets$ = this.select(({ dockets }) => dockets);
  readonly selectedDocketIds$ = this.select(
    ({ selectedDocketIds }) => selectedDocketIds
  );
  readonly pagination$ = this.select(({ pagination }) => pagination);
  readonly filters$ = this.select(({ filters }) => filters);
  readonly datasource$ = this.select(({ datasource }) => datasource);
  readonly summaryData$ = this.select(({ summaryData }) => summaryData);
  readonly anySelected$ = this.select(
    ({ selectedDocketIds }) => selectedDocketIds.length > 0
  );
  readonly showRolloverButton$ = this.select(
    ({ selectedDocketIds }) => selectedDocketIds.length > 0
  );

  allSelected$ = this.select(
    ({ selectedDocketIds, dockets }) =>
      selectedDocketIds.length === dockets.length && dockets.length > 0
  );

  readonly totalItems$ = this.pagination$.pipe(
    map((pagination) => pagination.count)
  );
  readonly pageSize$ = this.pagination$.pipe(
    map((pagination) => pagination.perPage)
  );
  readonly pageIndex$ = this.pagination$.pipe(
    map((pagination) => pagination.page)
  );

  // Updaters
  readonly setFilters = this.updater<{
    created?: { from: string | null; to: string | null };
    closed?: { from: string | null; to: string | null };
  }>((state, filters) => {
    const newFilters = { ...state.filters };

    if (filters.created) {
      newFilters.created = filters.created;
    }

    if (filters.closed) {
      newFilters.closed = filters.closed;
    }

    return {
      ...state,
      filters: newFilters,
      pagination: {
        ...state.pagination,
        page: 0
      }
    };
  });

  readonly toggleAllSelected = this.updater<boolean>((state, selectAll) => {
    if (selectAll) {
      return {
        ...state,
        selectedDocketIds: state.dockets.map((docket) => docket.id)
      };
    } else {
      return { ...state, selectedDocketIds: [] };
    }
  });

  readonly toggleSelected = this.updater<Docket>((state, docket) => {
    const isSelected = state.selectedDocketIds.includes(docket.id);

    if (isSelected) {
      return {
        ...state,
        selectedDocketIds: state.selectedDocketIds.filter(
          (id) => id !== docket.id
        )
      };
    } else {
      return {
        ...state,
        selectedDocketIds: [...state.selectedDocketIds, docket.id]
      };
    }
  });

  readonly setSort = this.updater<{
    sortBy: string;
    sortOrder: string;
  }>((state, sort) => ({
    ...state,
    pagination: {
      ...state.pagination,
      ...sort,
      page: 0,
      selectedDocketIds: []
    }
  }));

  readonly setPage = this.updater<{
    page: number;
    perPage: number;
  }>((state, page) => ({
    ...state,
    pagination: {
      ...state.pagination,
      ...page
    }
  }));

  readonly clearSelection = this.updater((state) => ({
    ...state,
    selectedDocketIds: []
  }));

  private readonly setReportLoading = this.updater<boolean>(
    (state, loading) => ({
      ...state,
      loadingResults: loading,
      selectedDocketIds: []
    })
  );

  generateReport() {
    this.setReportLoading(true);

    return this.setupDataSource();
  }

  private setupDataSource() {
    return this.patchState(
      combineLatest([
        this.filters$.pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          })
        ),
        this.pagination$.pipe(
          distinctUntilChanged((previous, current) => {
            return JSON.stringify(previous) === JSON.stringify(current);
          })
        )
      ]).pipe(
        tap(() => this.patchState({ selectedDocketIds: [] })),
        switchMap(([filters, pagination]) => {
          const usedFilters = this.prepareFilters(filters);

          return this.docketService
            .generateReport(
              {
                ...pagination,
                excludeDeleted: 1
              },
              usedFilters
            )
            .pipe(
              tapResponse(
                (response: any) => {
                  this.patchState({
                    dockets: response.data,
                    pagination: {
                      ...pagination,
                      count: response.count
                    },
                    loadingResults: false
                  });

                  // Load summary data
                  this.loadSummaryData(usedFilters);
                },
                (error) => {
                  console.error('Error loading dockets:', error);
                  this.patchState({ loadingResults: false });
                }
              ),
              map((response) => response.data)
            );
        }),
        map((dockets) => ({ datasource: dockets, dockets }))
      )
    );
  }

  private loadSummaryData(used: any) {
    this.docketService
      .generateReport({ summary: true, excludeDeleted: 1 }, used)
      .subscribe((results) => {
        this.patchState({ summaryData: results });
      });
  }

  private prepareFilters(filters: any) {
    const used: any = {};

    if (filters.created) {
      used.created = {
        type: 'fromToDate',
        value: filters.created
      };
    }

    if (filters.closed) {
      used.closed = {
        type: 'fromToDate',
        value: filters.closed
      };
    }

    used.status = filters.status;

    return used;
  }
}
